import { publicConfig } from "@/configs/public-config";
import { TokenService } from "@/providers/tokens/tokens.service";
import {
  buildFetchError,
  getFilenameFromContentDisposition,
  parseApiError,
  readErrorResponseBody,
} from "@/shared/helpers/api-file-download.helper";
import { type IApiResponseError } from "@/shared/interfaces/api-response.interface";

export interface IFetchDocumentWithCoordinatesResult {
  data?: { blob: Blob; filename: string };
  error?: IApiResponseError;
}

export async function fetchDocumentWithCoordinates(
  documentId: string,
  fallbackFilename: string,
): Promise<IFetchDocumentWithCoordinatesResult> {
  const baseUrl = `${publicConfig.apiUrl}/${publicConfig.apiTag}`;
  const url = `${baseUrl}/documents/${encodeURIComponent(documentId)}/export-with-coordinates`;
  const accessToken = TokenService.getAccessToken();
  const headers: HeadersInit = {};
  if (accessToken) {
    headers[publicConfig.authHeader] = `Bearer ${accessToken}`;
  }

  try {
    const response = await fetch(url, { method: "GET", headers });

    if (!response.ok) {
      const errorBody = await readErrorResponseBody(response);
      return { error: parseApiError(response, errorBody) };
    }

    const arrayBuffer = await response.arrayBuffer();
    const blobType = response.headers.get("Content-Type") ?? "application/octet-stream";
    const blob = new Blob([arrayBuffer], { type: blobType });
    const filename = getFilenameFromContentDisposition(
      response.headers.get("Content-Disposition"),
      fallbackFilename,
    );
    return { data: { blob, filename } };
  } catch (err) {
    return { error: buildFetchError(url, err) };
  }
}
